import {
  flatMapSpread,
  fromCondition,
  succeed,
  type Effect,
} from "@otto-ec/assets-core-utils/nano-effect";
import { toggle } from "@gr-exports/async/toggle";
import { getPagecluster } from "@gr-exports/async/misc";
import { random } from "@gr-common/async/tools";
import type { AgentConfigOptions } from "@elastic/apm-rum";
import { apmAsyncScope, noops } from "./utils";

const log = apmAsyncScope.scope("config");

export interface SamplingConfig {
  /**
 *
 *
 *
 */
  active: number;

  /**
 *
 */
  config: AgentConfigOptions;
}

/**
 *
 */
export interface SamplingEnvConfig {
  /**
 *
 *
 */
  [pagecluster: string]: Partial<SamplingConfig>;
  /**
 *
 *
 */
  default: SamplingConfig;
}

/**
 *
 *
 */
export type InitConfig = [
  config: AgentConfigOptions,
  labels: Labels,
  isWithinSampligRate: boolean,
  isToggleEnabled: boolean,
  isAtOttoDe: boolean,
];

/**
 *
 */
export const sampligRates: Record<"live" | "develop", SamplingEnvConfig> = {
  live: {
    Artikeldetailseite: { active: 0.1, config: { transactionSampleRate: 0.0001 } },

    Suchergebnisseite: { active: 0.2 },

    Storefront: { active: 0.4 },
    Produktliste: { active: 0.4 },

    Warenkorb: { active: 0.45 },

    AftersalesOrders: { active: 0.5 },

    Wishlist: { active: 0.7 },
    Login: { active: 0.7 },
    Paymentinfo: { active: 0.7 },

    /*                              */
    /*                           */
    /*                             */

    default: {
      active: 1,
      config: {
        transactionSampleRate: 0.001,
        /*                                      */
        disableInstrumentations: ["fetch", "xmlhttprequest"],
      },
    },
  },
  develop: {
    default: { active: 1, config: { transactionSampleRate: 0.5 } },
  },
};

/**
 *
 *
 */
export const computeApmActivationRequirements = flatMapSpread(((
  config,
  labels,
  isInRate,
  isToggleActive,
  isAtOttoDe,
) =>
  fromCondition(
    isInRate && isToggleActive && isAtOttoDe,
    () => (log.info("APM Agent active", { config, labels }), [config, labels] as const),
    () => (log.info("APM Agent inactive", { isInRate, isToggleActive, isAtOttoDe }), noops),
  )) satisfies (...a: InitConfig) => unknown);

/**
 *
 */
export const getApmInitializationConfiguration = (
  rate = random(),
  pc = getPagecluster(),
  host = location.hostname,
): Effect<InitConfig, never> => {
  /*                                                                                  */
  const envRates = sampligRates[import.meta.env.VITE_OTTO_INSTALLATION_ENV];
  if (!envRates) {
    log.warn(
      `Invalid environment configuration '${
        import.meta.env.VITE_OTTO_INSTALLATION_ENV
      }', disable APM agent`,
    );
    return succeed([{}, {}, false, false, false]);
  }

  /*                                         */
  const pagecluster = pc ?? "unknown";
  /*                                                      */
  const activationSampleRate = envRates[pagecluster]?.active ?? envRates.default.active;
  /*                                                                    */
  const pageclusterConfig = { ...envRates.default.config, ...envRates[pagecluster]?.config };

  const { labels: serverLabels = {}, ...serverConfig } = { ...window.__APM__ };

  /*                                                                 */
  const labels = {
    sample_rate_active: activationSampleRate,
    sample_rate_transaction: pageclusterConfig.transactionSampleRate ?? 1,
    pagecluster,
    ...serverLabels,
  };

  const agentConfig: AgentConfigOptions = {
    serviceName: import.meta.env.VITE_OTTO_INSTALLATION_NAME,
    serverUrl: import.meta.env.VITE_ELASTIC_APM_SERVER_URL,
    environment: import.meta.env.VITE_OTTO_INSTALLATION_ENV,

    /*                                        */
    /*                                             */
    pageLoadTransactionName: pc ?? undefined,

    /*                                                                          */
    pageLoadSampled: true,

    /*                                         */
    ...pageclusterConfig,
    /*                                                     */
    ...serverConfig,
  };

  /*                                                            */
  const activationFlags = serverConfig.active
    ? ([true, true, true] as const)
    : ([
        rate <= activationSampleRate,
        toggle.get("gr_apm_agent_enable", true),
        host.endsWith("otto.de"),
      ] as const);

  return succeed([agentConfig, labels, ...activationFlags]);
};
